import React, { useRef, useState, useEffect } from "react";
import S from "./Footer.style";
import useFooterData from "./useFooterData";
import { nlToBr } from "@/src/helpers";
import { useLocalization } from "@/contexts/Localization";
import { Script } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";

type FooterProps = {};

declare const window: {
  HubSpotConversations: any;
  hsConversationsSettings: {};
  hsConversationsOnReady: (() => void)[];
  location: {
    hash: string;
    href: string;
  },
  hbspt: {
    forms: {
      create: any;
    };
    HubSpotConversations: any;
    hsConversationsSettings: any;
    hsConversationsOnReady: any;
    location: any;
  }
};

const Footer: React.FC<FooterProps> = () => {
  const { language } = useLocalization();
  const data = useFooterData(language);
  const { footerBlock1, footerBlock2, footerBlock3, footerMenu } = data;
  const ref = useRef<HTMLDivElement>(null);
  const [initiated, setInitiated] = useState(false);
  const [isNewsletterPage, setIsNewsletterPage] = useState(false);

  useEffect(() => {
    if (/nyhetsbrev/.test(window.location.href)) {
      setIsNewsletterPage(true);
    }

    if (window.hbspt?.forms?.create) {
      if (ref.current && !initiated) {
        setInitiated(true);
        if (language == 'sv') {
          window.hbspt.forms.create({
            target: "#hubspot-newsletter",
            region: "na1",
            portalId: "6464302",
            formId: "17573771-9155-431c-85df-c7f55e235cd6",
          });
        } else {
          window.hbspt.forms.create({
            target: "#hubspot-newsletter",
            region: "eu1",
            portalId: "6464302",
            formId: "64980602-996e-42ef-8d27-f9d0a4f1f721"
          });
        }
      }
    }

    function onConversationsAPIReady() {
      window.HubSpotConversations.on('conversationStarted', (payload: any) => {
        window.location.hash = `conversation=${payload.conversation.conversationId}`;
      })
    }

    window.hsConversationsSettings = {};

    if (window.HubSpotConversations) {
      onConversationsAPIReady();
    } else {

      window.hsConversationsOnReady = [onConversationsAPIReady];
    }

  }, [ref, initiated, isNewsletterPage]);

  const logo = footerBlock1?.logo?.gatsbyImage
    ? getImage(footerBlock1.logo.gatsbyImage)
    : null;

  return (
    <S.FooterContainer>
      {
        isNewsletterPage ?
          <S.Block isNewsletterPage={isNewsletterPage}>
            <S.H5>{footerBlock3.title}</S.H5>
            <S.InputContainer>
              <Script
                type="text/javascript"
                charSet="UTF-8"
                src="//js-eu1.hsforms.net/forms/v2.js"
              />
              <S.Input id="hubspot-newsletter" ref={ref} />
            </S.InputContainer>
          </S.Block>
          :
          ''
      }
      <div className="main-inner">
        <S.Footer>
          <S.GridContainer>
            <S.Block>
              {logo && (
                <GatsbyImage
                  className="gatsby-image"
                  image={logo}
                  alt={footerBlock1.logo.altText}
                />
              )}
              <S.Paragraph>{nlToBr(footerBlock1.text)}</S.Paragraph>
            </S.Block>
            <S.LinkBlock>
              <S.ParagraphSmall>
                <S.GatsbyLink2
                  to={footerBlock1?.link1?.url}
                  target={footerBlock1?.link1?.target}
                >
                  {footerBlock1?.link1?.title}
                </S.GatsbyLink2>
                <S.GatsbyLink2
                  to={footerBlock1?.link2?.url}
                  target={footerBlock1?.link2?.target}
                >
                  {footerBlock1?.link2?.title}
                </S.GatsbyLink2>
              </S.ParagraphSmall>
            </S.LinkBlock>
          </S.GridContainer>
          <S.GridContainer>
            <S.GridBlockLinks>
              {footerMenu?.menuBlock?.map((item, index) => (
                <S.GridItem key={index}>
                  <S.GatsbyLink to={item.title.url} target={item.title.target}>
                    <S.H5>{item.title.title}</S.H5>
                  </S.GatsbyLink>
                  {item.linkBlock.map((i, index) => {
                    return (
                      <S.GatsbyLink
                        to={i.link.url}
                        target={i.link.target}
                        key={index}
                      >
                        <S.DetailsSmall>{i.link.title}</S.DetailsSmall>
                      </S.GatsbyLink>
                    );
                  })}
                </S.GridItem>
              ))}
            </S.GridBlockLinks>
            <S.GridBlock>
              <S.Block>
                <S.H5>{footerBlock2.title}</S.H5>
                <S.Paragraph>{nlToBr(footerBlock2.text)}</S.Paragraph>
              </S.Block>
              <S.Block className="space-between">
                {!isNewsletterPage ?
                  <S.Block>
                    <S.H5>{footerBlock3.title}</S.H5>
                    <S.InputContainer>
                      <Script
                        type="text/javascript"
                        charSet="UTF-8"
                        src="//js-eu1.hsforms.net/forms/v2.js"
                      />
                      <S.Input id="hubspot-newsletter" ref={ref} />
                    </S.InputContainer>
                  </S.Block>
                  :
                  ''}
                <S.Block>
                  <S.H5>Social media</S.H5>
                  <S.LinkBlock>
                    {footerBlock3?.socialMedia?.map((link, index) => {
                      const icon = link.icon.gatsbyImage
                        ? getImage(link.icon.gatsbyImage)
                        : null;
                      return (
                        <S.ALink
                          href={link.account.url}
                          target={link.account.target}
                          key={index}
                        >
                          {icon && (
                            <GatsbyImage
                              className="gatsby-image-social"
                              image={icon}
                              alt={footerBlock1.logo.altText}
                            />
                          )}
                          <S.H6>{link.account.title}</S.H6>
                        </S.ALink>
                      );
                    })}
                  </S.LinkBlock>
                </S.Block>
              </S.Block>
            </S.GridBlock>
          </S.GridContainer>
        </S.Footer>
      </div>
    </S.FooterContainer>
  );
};

export default Footer;
